import { useGetOwnershipMoneySourceDetailsMutation } from "@/service/OwnerService";
import { Spinner, ErrorMessage, Heading } from "@/common/components";
import { useEffect, useContext, useState, useSelector } from "@/common/hooks";
import { PrivateContext } from "@/routes/PrivateRoute";
import { setMoneySourceDetailsValues } from "@/store/ownersSlice";
import { amountFormatter } from "@/utils/Utilities";

const MoneySourceDetails = () => {
  const { labels, productSponsorId, accessProfileId, dispatch } =
    useContext(PrivateContext);

  const [moneySourceDetails, setMoneySourceDetails] = useState({
    employeePretax: { cashOutAmount: 0, incomeShareCount: 0 },
    employerPretax: { cashOutAmount: 0, incomeShareCount: 0 },
    employeeAftertax: { cashOutAmount: 0, incomeShareCount: 0 },
    incomeShareCountTotal: 0,
    cashOutValueTotal: 0,
    previousBusinessDay: null,
  });
  const [ownershipInquiryDetails, setOwnershipInquiryDetails] = useState();

  const [getOwnershipMoneySourceDetailsApi, { isLoading: isFetching, error }] =
    useGetOwnershipMoneySourceDetailsMutation();
  const moneySourceDetailsFromStore = useSelector(
    (state) => state?.ownersReducer?.moneySourceDetailsValues
  );
  const ownershipInquiryFromStore = useSelector(
    (state) => state?.ownersReducer?.ownershipInquiryResult
  );

  const {
    lineOfBusinessId,
    ownershipTypeCode,
    ownerTaxId,
    planId,
    externalProductId,
    recordKeeperName,
    recordKeepingSystemName,
  } = ownershipInquiryDetails || {};

  const getOwnershipMoneySourceDetail = () => {
    const getOwnershipMoneySourceDetailPayload = {
      lineOfBusinessId,
      ownershipTypeCode,
      ownerTaxId: ownerTaxId,
      planId,
      externalProductId,
      recordKeeperName,
      recordKeepingSystemName,
      productSponsorId,
      accessProfileId,
    };

    getOwnershipMoneySourceDetailsApi(getOwnershipMoneySourceDetailPayload)
      .unwrap()
      .then((res) => {
        if (!res.inError) {
          dispatch(setMoneySourceDetailsValues(res));
        }
      });
  };

  useEffect(() => {
    if (ownershipInquiryDetails) {
      getOwnershipMoneySourceDetail();
    }
  }, [ownershipInquiryDetails]);

  useEffect(() => {
    if (ownershipInquiryFromStore) {
      setOwnershipInquiryDetails(ownershipInquiryFromStore);
    }
  }, [ownershipInquiryFromStore]);

  useEffect(() => {
    if (moneySourceDetailsFromStore) {
      setMoneySourceDetails((prev) => {
        let updatedDetails = { ...prev };

        moneySourceDetailsFromStore?.ownershipMoneySourceBeans.forEach(
          (item) => {
            if (item?.moneySourceShortName === "EEPT") {
              updatedDetails.employeePretax = {
                cashOutAmount: amountFormatter(item?.cashOutAmount),
                incomeShareCount: item?.incomeShareCount,
              };
            } else if (item?.moneySourceShortName === "ERPT") {
              updatedDetails.employerPretax = {
                cashOutAmount: amountFormatter(item?.cashOutAmount),
                incomeShareCount: item?.incomeShareCount,
              };
            } else if (item?.moneySourceShortName === "EEAT") {
              updatedDetails.employeeAftertax = {
                cashOutAmount: amountFormatter(item?.cashOutAmount),
                incomeShareCount: item?.incomeShareCount,
              };
            }
          }
        );
        updatedDetails.incomeShareCountTotal =
          ownershipInquiryDetails?.incomeShareCount;
        updatedDetails.cashOutValueTotal = amountFormatter(
          ownershipInquiryDetails?.cashOutValueAmount
        );
        updatedDetails.previousBusinessDay =
          moneySourceDetailsFromStore?.previousBusinessDay;

        return updatedDetails;
      });
    }
  }, [moneySourceDetailsFromStore]);

  return (
    <>
      {isFetching && <Spinner />}
      <ErrorMessage error={error} wrapperClassName="mb-3" />
      <div className="py-2 my-2">
        <Heading
          text={labels?.ownerInquiry?.currMoneySourceTotals}
          wrapperClassNames="h4 border-bottom-1 pb-1 mb-0"
          title={false}
        />
        {moneySourceDetails?.previousBusinessDay && (
          <span>{`(${labels?.ownerInquiry?.asOfCloseOfBusiness} ${moneySourceDetails?.previousBusinessDay})`}</span>
        )}
      </div>
      <div className="d-md-block d-none">
        <div className="row mt-3">
          <div className="col-md-4">
            <p></p>
          </div>
          <div className="col-md-4">
            <p>{labels?.ownerInquiry?.incomeShares}:</p>
          </div>
          <div className="col-md-4">
            <p>{labels?.ownerInquiry?.cashOutValue}:</p>
          </div>
        </div>
        <div className="row">
          <div className="col-md-4">
            <p>{labels?.ownerInquiry?.employeePretax}</p>
          </div>
          <div className="col-md-4">
            <p>{moneySourceDetails?.employeePretax?.incomeShareCount}</p>
          </div>
          <div className="col-md-4">
            <p>{moneySourceDetails?.employeePretax?.cashOutAmount}</p>
          </div>
        </div>
        <div className="row">
          <div className="col-md-4">
            <p>{labels?.ownerInquiry?.employerPretax}</p>
          </div>
          <div className="col-md-4">
            <p>{moneySourceDetails?.employerPretax?.incomeShareCount}</p>
          </div>
          <div className="col-md-4">
            <p>{moneySourceDetails?.employerPretax?.cashOutAmount}</p>
          </div>
        </div>
        <div className="row">
          <div className="col-md-4">
            <p>{labels?.ownerInquiry?.employeeAftertax}</p>
          </div>
          <div className="col-md-4">
            <p>{moneySourceDetails?.employeeAftertax?.incomeShareCount}</p>
          </div>
          <div className="col-md-4">
            <p>{moneySourceDetails?.employeeAftertax?.cashOutAmount}</p>
          </div>
        </div>
        <div className="row">
          <div className="col-md-4">
            <p className="fw-bold">{labels?.ownerInquiry?.total}</p>
          </div>
          <div className="col-md-4">
            <p className="fw-bold">
              {moneySourceDetails?.incomeShareCountTotal}
            </p>
          </div>
          <div className="col-md-4">
            <p className="fw-bold">{moneySourceDetails?.cashOutValueTotal}</p>
          </div>
        </div>
      </div>
      <div style={{ maxWidth: "500px" }} className="mx-auto my-3 d-md-none">
        <p>Type: {labels?.ownerInquiry?.incomeShares}</p>
        <div className="d-flex align-items-center justify-content-between border-bottom my-2">
          <div>
            <p>{labels?.ownerInquiry?.employeePretax}</p>
            <p>{labels?.ownerInquiry?.employerPretax}</p>
            <p>{labels?.ownerInquiry?.employeeAftertax}</p>
            <p className="fw-bold">{labels?.ownerInquiry?.total}</p>
          </div>
          <div>
            <p>{moneySourceDetails?.employeePretax?.incomeShareCount}</p>
            <p>{moneySourceDetails?.employerPretax?.incomeShareCount}</p>
            <p>{moneySourceDetails?.employeeAftertax?.incomeShareCount}</p>
            <p className="fw-bold">
              {moneySourceDetails?.incomeShareCountTotal}
            </p>
          </div>
        </div>
        <p>Type: {labels?.ownerInquiry?.cashOutValue}</p>
        <div className="d-flex align-items-center justify-content-between">
          <div>
            <p>{labels?.ownerInquiry?.employeePretax}</p>
            <p>{labels?.ownerInquiry?.employerPretax}</p>
            <p>{labels?.ownerInquiry?.employeeAftertax}</p>
            <p className="fw-bold">{labels?.ownerInquiry?.total}</p>
          </div>
          <div>
            <p className="text-end">
              {moneySourceDetails?.employeePretax?.cashOutAmount}
            </p>
            <p className="text-end">
              {moneySourceDetails?.employerPretax?.cashOutAmount}
            </p>
            <p className="text-end">
              {moneySourceDetails?.employeeAftertax?.cashOutAmount}
            </p>
            <p className="fw-bold text-end">
              {moneySourceDetails?.cashOutValueTotal}
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default MoneySourceDetails;
